import {
  useCallback,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type { ProductsSeenMap } from "@/types/frontend-types";
import type { SessionCollab } from "./context";
import {
  selectLatestCollabCreatedAt,
} from "./session-date-selectors";
import {
  mergeCollabMap,
  selectAnimatedCollabArrivalIds,
  selectCollabIds,
  selectNewIds,
} from "./session-data-provider-utils";

interface UseSessionCollabMergesInput {
  collabsByProductRef: MutableRefObject<Record<string, string[]>>;
  chatMessageIdsRef: MutableRefObject<string[]>;
  ownMessageIdsRef: MutableRefObject<Set<string>>;
  setCollabs: Dispatch<SetStateAction<Record<string, SessionCollab>>>;
  setCollabsByProduct: Dispatch<SetStateAction<Record<string, string[]>>>;
  setChatMessages: Dispatch<SetStateAction<Record<string, SessionCollab>>>;
  setChatMessageIds: Dispatch<SetStateAction<string[]>>;
  setNewMessageIds: Dispatch<SetStateAction<Set<string>>>;
  updateProductsSeen: (input: {
    productId: string;
    patch: Partial<ProductsSeenMap[string]>;
  }) => void;
  updateProductCollabsSinceTimestamp: (input: {
    productId: string;
    collabsSince: string;
  }) => void;
  tagOwnCollabsAndAdvanceRead: (input: {
    productId: string | null;
    collabs: readonly SessionCollab[];
  }) => void;
  flagCollabArrivalsAndAncestors: (input: {
    productId: string;
    newCollabIds: string[];
    canFlash: boolean;
  }) => void;
  pushMemberActivitiesFromCollabs: (input: {
    collabs: readonly SessionCollab[];
    arrivalIds: Set<string>;
  }) => void;
}

interface SessionCollabMergeActions {
  replaceProductCollabItems: (input: {
    productId: string;
    items: readonly SessionCollab[];
  }) => void;
  mergePolledChatItems: (input: {
    items: SessionCollab[];
    canAnimate: boolean;
  }) => void;
  mergePolledProductCollabItems: (input: {
    productId: string;
    items: SessionCollab[];
    canFlash: boolean;
  }) => void;
}

interface MergeProductCollabStateInput {
  productId: string;
  items: readonly SessionCollab[];
  incomingIds: readonly string[];
  setCollabs: Dispatch<SetStateAction<Record<string, SessionCollab>>>;
  setCollabsByProduct: Dispatch<SetStateAction<Record<string, string[]>>>;
}

interface ApplyProductCollabReadMarkersInput {
  productId: string;
  items: readonly SessionCollab[];
  updateProductsSeen: UseSessionCollabMergesInput["updateProductsSeen"];
  tagOwnCollabsAndAdvanceRead: UseSessionCollabMergesInput["tagOwnCollabsAndAdvanceRead"];
}

interface AnnounceProductCollabArrivalsInput {
  productId: string;
  items: readonly SessionCollab[];
  canFlash: boolean;
  newCollabIds: string[];
  flagCollabArrivalsAndAncestors: UseSessionCollabMergesInput["flagCollabArrivalsAndAncestors"];
  pushMemberActivitiesFromCollabs: UseSessionCollabMergesInput["pushMemberActivitiesFromCollabs"];
  ownMessageIdsRef: UseSessionCollabMergesInput["ownMessageIdsRef"];
  setNewMessageIds: UseSessionCollabMergesInput["setNewMessageIds"];
}

export function useSessionCollabMerges(
  input: UseSessionCollabMergesInput,
): SessionCollabMergeActions {
  const replaceProductCollabItems = useReplaceProductCollabItemsAction(input);
  const mergePolledChatItems = useMergePolledChatItemsAction(input);
  const mergePolledProductCollabItems =
    useMergePolledProductCollabItemsAction(input);

  return {
    replaceProductCollabItems,
    mergePolledChatItems,
    mergePolledProductCollabItems,
  };
}

function useReplaceProductCollabItemsAction({
  setCollabs,
  setCollabsByProduct,
  updateProductsSeen,
  updateProductCollabsSinceTimestamp,
}: UseSessionCollabMergesInput): SessionCollabMergeActions["replaceProductCollabItems"] {
  return useCallback(
    (input: { productId: string; items: readonly SessionCollab[] }): void => {
      setCollabs((prev) =>
        mergeCollabMap({
          previous: prev,
          items: input.items,
        }),
      );
      setCollabsByProduct((prev) => ({
        ...prev,
        [input.productId]: selectCollabIds(input.items),
      }));
      updateProductsSeen({
        productId: input.productId,
        patch: {
          lastAddedCollabs: selectLatestCollabCreatedAt({
            items: input.items,
          }),
        },
      });
      updateProductCollabsSinceTimestamp({
        productId: input.productId,
        collabsSince: new Date().toISOString(),
      });
    },
    [
      setCollabs,
      setCollabsByProduct,
      updateProductCollabsSinceTimestamp,
      updateProductsSeen,
    ],
  );
}

function useMergePolledChatItemsAction({
  chatMessageIdsRef,
  ownMessageIdsRef,
  setChatMessages,
  setChatMessageIds,
  setNewMessageIds,
  tagOwnCollabsAndAdvanceRead,
  pushMemberActivitiesFromCollabs,
}: UseSessionCollabMergesInput): SessionCollabMergeActions["mergePolledChatItems"] {
  return useCallback(
    (input: { items: SessionCollab[]; canAnimate: boolean }): void => {
      if (input.items.length === 0) return;

      const incomingIds = selectCollabIds(input.items);
      const newChatIds = selectNewIds({
        incomingIds,
        existingIds: chatMessageIdsRef.current,
      });
      setChatMessages((prev) =>
        mergeCollabMap({
          previous: prev,
          items: input.items,
        }),
      );
      setChatMessageIds((prev) => [
        ...prev,
        ...selectNewIds({
          incomingIds,
          existingIds: prev,
        }),
      ]);

      queueAnimatedCollabArrivals({
        newIds: newChatIds,
        ownMessageIds: ownMessageIdsRef.current,
        canAnimate: input.canAnimate,
        setNewMessageIds,
      });

      tagOwnCollabsAndAdvanceRead({
        productId: null,
        collabs: input.items,
      });
      pushMemberActivitiesFromCollabs({
        collabs: input.items,
        arrivalIds: selectNewChatIdSet({
          incomingIds,
          existingIds: chatMessageIdsRef.current,
        }),
      });
    },
    [
      chatMessageIdsRef,
      ownMessageIdsRef,
      pushMemberActivitiesFromCollabs,
      setChatMessageIds,
      setChatMessages,
      setNewMessageIds,
      tagOwnCollabsAndAdvanceRead,
    ],
  );
}

function useMergePolledProductCollabItemsAction({
  collabsByProductRef,
  ownMessageIdsRef,
  setCollabs,
  setCollabsByProduct,
  setNewMessageIds,
  updateProductsSeen,
  tagOwnCollabsAndAdvanceRead,
  flagCollabArrivalsAndAncestors,
  pushMemberActivitiesFromCollabs,
}: UseSessionCollabMergesInput): SessionCollabMergeActions["mergePolledProductCollabItems"] {
  return useCallback(
    (input: {
      productId: string;
      items: SessionCollab[];
      canFlash: boolean;
    }): void => {
      if (input.items.length === 0) return;

      const incomingIds = selectCollabIds(input.items);
      const newCollabIds = selectNewIds({
        incomingIds,
        existingIds: collabsByProductRef.current[input.productId] ?? [],
      });

      mergeProductCollabState({
        productId: input.productId,
        items: input.items,
        incomingIds,
        setCollabs,
        setCollabsByProduct,
      });
      applyProductCollabReadMarkers({
        productId: input.productId,
        items: input.items,
        updateProductsSeen,
        tagOwnCollabsAndAdvanceRead,
      });
      announceProductCollabArrivals({
        productId: input.productId,
        items: input.items,
        newCollabIds,
        canFlash: input.canFlash,
        flagCollabArrivalsAndAncestors,
        pushMemberActivitiesFromCollabs,
        ownMessageIdsRef,
        setNewMessageIds,
      });
    },
    [
      collabsByProductRef,
      flagCollabArrivalsAndAncestors,
      ownMessageIdsRef,
      pushMemberActivitiesFromCollabs,
      setCollabs,
      setCollabsByProduct,
      setNewMessageIds,
      tagOwnCollabsAndAdvanceRead,
      updateProductsSeen,
    ],
  );
}

function mergeProductCollabState({
  productId,
  items,
  incomingIds,
  setCollabs,
  setCollabsByProduct,
}: MergeProductCollabStateInput): void {
  setCollabs((prev) =>
    mergeCollabMap({
      previous: prev,
      items,
    }),
  );
  setCollabsByProduct((prev) =>
    selectMergedCollabsByProduct({
      previous: prev,
      productId,
      incomingIds,
    }),
  );
}

function applyProductCollabReadMarkers({
  productId,
  items,
  updateProductsSeen,
  tagOwnCollabsAndAdvanceRead,
}: ApplyProductCollabReadMarkersInput): void {
  updateProductsSeen({
    productId,
    patch: {
      lastAddedCollabs: selectLatestCollabCreatedAt({ items }),
    },
  });
  tagOwnCollabsAndAdvanceRead({
    productId,
    collabs: items,
  });
}

function announceProductCollabArrivals({
  productId,
  items,
  canFlash,
  newCollabIds,
  flagCollabArrivalsAndAncestors,
  pushMemberActivitiesFromCollabs,
  ownMessageIdsRef,
  setNewMessageIds,
}: AnnounceProductCollabArrivalsInput): void {
  flagCollabArrivalsAndAncestors({
    productId,
    newCollabIds,
    canFlash,
  });
  pushMemberActivitiesFromCollabs({
    collabs: items,
    arrivalIds: new Set(newCollabIds),
  });
  queueAnimatedCollabArrivals({
    newIds: newCollabIds,
    ownMessageIds: ownMessageIdsRef.current,
    canAnimate: canFlash,
    setNewMessageIds,
  });
}

function queueAnimatedCollabArrivals(input: {
  newIds: readonly string[];
  ownMessageIds: ReadonlySet<string>;
  canAnimate: boolean;
  setNewMessageIds: UseSessionCollabMergesInput["setNewMessageIds"];
}): void {
  const animatedIds = selectAnimatedCollabArrivalIds(input);
  if (animatedIds.length === 0) {
    return;
  }

  input.setNewMessageIds((previousIds) => {
    const nextIds = new Set(previousIds);
    animatedIds.forEach((id) => nextIds.add(id));
    return nextIds;
  });
}

function selectNewChatIdSet(input: {
  incomingIds: readonly string[];
  existingIds: readonly string[];
}): Set<string> {
  return new Set(
    selectNewIds({
      incomingIds: input.incomingIds,
      existingIds: input.existingIds,
    }),
  );
}

function selectMergedCollabsByProduct(input: {
  previous: Record<string, string[]>;
  productId: string;
  incomingIds: readonly string[];
}): Record<string, string[]> {
  const existingIds = input.previous[input.productId] || [];
  return {
    ...input.previous,
    [input.productId]: [
      ...existingIds,
      ...selectNewIds({
        incomingIds: input.incomingIds,
        existingIds,
      }),
    ],
  };
}
